import { useNavigate, Link } from 'react-router-dom';
import { useColorContext } from '../contexts/ColorContext';
import { useColorHistory } from '../hooks/useColorHistory';
import { ColorInput } from '../components/ui/ColorInput';
import { FormatCell } from '../components/ui/FormatCell';
import {
  Palette, Eye, GraduationCap, Layers,
  Printer, ShieldCheck, Camera, Moon,
  Award, Search, Box,
} from 'lucide-react';
import { type ColorResult } from '../lib/colorEngine';
import '../styles/home.css';

/* ── Quick-format subset shown below the hero input ─────────────────── */
const QUICK_FORMATS: { key: keyof ColorResult; label: string }[] = [
  { key: 'hex',   label: 'HEX' },
  { key: 'rgb',   label: 'RGB' },
  { key: 'hsl',   label: 'HSL' },
  { key: 'oklch', label: 'OKLCH' },
  { key: 'lab',   label: 'CIELAB' },
  { key: 'cmyk',  label: 'CMYK' },
];

/* ── Intent cards — each one is a persona entry point ───────────────── */
const INTENTS = [
  {
    id: 'palette',
    icon: Palette,
    title: 'Build a palette',
    desc: 'Generate harmonies, extract from images, or start from a base color.',
    to: '/palette',
  },
  {
    id: 'accessibility',
    icon: Eye,
    title: 'Check accessibility',
    desc: 'Test WCAG contrast, simulate color blindness, audit full palettes.',
    to: '/accessibility',
  },
  {
    id: 'education',
    icon: GraduationCap,
    title: 'Learn color theory',
    desc: 'Interactive lessons on color spaces, harmonies, and perception.',
    to: '/education',
  },
  {
    id: 'tokens',
    icon: Layers,
    title: 'Manage design tokens',
    desc: 'Generate and export tokens for CSS, Tailwind, iOS, Android, and more.',
    to: '/tokens',
  },
] as const;

/* ── Workflow cards — composite paths across multiple tools ──────────── */
const WORKFLOWS = [
  {
    id: 'wf-print',
    icon: Printer,
    title: 'Prep for print',
    desc: 'Convert to CMYK with ICC profiles, check gamut, simulate on substrates, find nearest Pantone.',
    to: '/print',
    steps: ['Formats', 'Print & ICC', 'Substrate', 'Nearest'],
  },
  {
    id: 'wf-brand-a11y',
    icon: ShieldCheck,
    title: 'Make brand colors accessible',
    desc: 'Audit palette contrast, auto-fix failing pairs, verify identity with ΔE, export compliant tokens.',
    to: '/audit',
    steps: ['Palette', 'Audit', 'Accessibility', 'Compare', 'Tokens'],
  },
  {
    id: 'wf-image-palette',
    icon: Camera,
    title: 'Image → production palette',
    desc: 'Extract colors from a photo, refine with harmony rules, check accessibility, export tokens.',
    to: '/extract',
    steps: ['Extract', 'Palette', 'Audit', 'Tokens'],
  },
  {
    id: 'wf-dark-mode',
    icon: Moon,
    title: 'Adapt for dark mode',
    desc: 'Explore tints and shades, test contrast on dark backgrounds, compare ΔE to originals.',
    to: '/encyclopedia',
    steps: ['Encyclopedia', 'Accessibility', 'Compare', 'Palette'],
  },
  {
    id: 'wf-score',
    icon: Award,
    title: 'Score a palette',
    desc: 'Rate your palette against use-case criteria — dark UI, data viz, marketing — and get actionable fixes.',
    to: '/audit',
    steps: ['Palette', 'Audit', 'Fitness Score', 'Accessibility'],
  },
  {
    id: 'wf-identify',
    icon: Search,
    title: 'Identify a color',
    desc: 'Find the nearest named color, explore semantic associations, and get a full encyclopedia breakdown.',
    to: '/nearest',
    steps: ['Nearest', 'Semantic', 'Encyclopedia'],
  },
  {
    id: 'wf-materials',
    icon: Box,
    title: 'Simulate on materials',
    desc: 'See how your color renders on paper, cardboard, plastic, or fabric with multi-layer ink simulation.',
    to: '/print',
    steps: ['Formats', 'Print & ICC', 'Substrate', 'Layer Stack'],
  },
] as const;

/* ── Component ──────────────────────────────────────────────────────── */
export function HomeTool() {
  const navigate = useNavigate();
  const {
    input, setInput, inputRef,
    displayResult, copiedField, copyToClipboard,
    formatValue, getCssFunctional,
  } = useColorContext();

  const { history } = useColorHistory();
  const recentSwatches = history.slice(0, 16);

  const hasColor = !!displayResult;

  return (
    <div className="home">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="home__hero">
        <h1 className="home__tagline">
          Professional color, in your browser
        </h1>
        <p className="home__subtitle">
          Convert, explore, and manage colors across 15+ color spaces — all offline.
        </p>

        <div className="home__input-wrap">
          <ColorInput
            value={input}
            onChange={setInput}
            inputRef={inputRef}
            placeholder="Paste any color — hex, rgb(), oklch(), name…"
            hideStatus
          />
          {!hasColor && (
            <div className="home__input-hint">
              Try <code>#3b82f6</code>, <code>oklch(0.7 0.15 250)</code>, or <code>dodgerblue</code>
            </div>
          )}
        </div>
      </section>

      {/* ── Quick formats (visible when a color is entered) ─────────── */}
      {hasColor && (
        <section className="home__quick-formats">
          {QUICK_FORMATS.map(({ key, label }) => {
            const value = displayResult[key as keyof ColorResult];
            if (value === undefined || value === null) return null;
            const formatted = formatValue(value, 0);
            const cssValue = getCssFunctional(key, value);
            return (
              <FormatCell
                key={key}
                label={label}
                value={formatted}
                cssValue={cssValue || null}
                copied={copiedField === key}
                onCopy={() => copyToClipboard(cssValue || formatted, key)}
              />
            );
          })}
          <Link to="/formats" className="home__all-formats-link">
            All 19 formats →
          </Link>
        </section>
      )}

      {/* ── Intent cards ──────────────────────────────────────────────── */}
      <section className="home__intents">
        {INTENTS.map(({ id, icon: Icon, title, desc, to }) => (
          <button
            key={id}
            className="home__intent-card"
            onClick={() => navigate(to, { state: { from: 'home' } })}
            type="button"
          >
            <div className="home__intent-icon">
              <Icon size={18} />
            </div>
            <h2 className="home__intent-title">{title}</h2>
            <p className="home__intent-desc">{desc}</p>
          </button>
        ))}
      </section>

      {/* ── Workflow cards ─────────────────────────────────────────────── */}
      <section className="home__workflows">
        <div className="home__section-label">Workflows</div>
        <div className="home__workflow-grid">
          {WORKFLOWS.map(({ id, icon: Icon, title, desc, to, steps }) => (
            <button
              key={id}
              className="home__workflow-card"
              onClick={() => navigate(to, { state: { from: 'home' } })}
              type="button"
            >
              <div className="home__workflow-header">
                <div className="home__workflow-icon">
                  <Icon size={16} />
                </div>
                <h3 className="home__workflow-title">{title}</h3>
              </div>
              <p className="home__workflow-desc">{desc}</p>
              <div className="home__workflow-steps">
                {steps.map((step, i) => (
                  <span key={step} className="home__workflow-step">
                    {i > 0 && <span className="home__workflow-arrow">→</span>}
                    {step}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Recent history ────────────────────────────────────────────── */}
      {recentSwatches.length > 0 && (
        <section className="home__recent">
          <div className="home__recent-label">Recently used</div>
          <div className="home__recent-strip">
            {recentSwatches.map((entry) => (
              <button
                key={entry.id ?? entry.hex}
                className="home__recent-swatch"
                style={{ backgroundColor: entry.hex }}
                title={entry.hex}
                onClick={() => setInput(entry.hex)}
                type="button"
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
